/**
 * useTutorLiveTraces — les traces de la salle du tuteur, pour lire la prochaine occurrence d'un gel ou
 * d'un « Connexion… » qui ne finit pas. Posées avec le correctif d'hypothèse de `tutorMediaAccess` (gel non
 * reproduit, voir la PR #2957) :
 *  - **les journaux de LiveKit**, `warn` et au-dessus, en fils d'Ariane (`livekit → warn`, l'événement est
 *    la ligne du SDK) : « negotiation timed out », « unable to set offer », les raisons d'une reconnexion.
 *    `setLogExtension` n'a pas de retrait et s'empile à chaque appel : il est posé une fois pour l'app, et
 *    ne transmet qu'à la salle montée (rien une fois démontée). Plafonnées par entrée, pour qu'une
 *    reconnexion qui boucle ne chasse pas les autres fils d'Ariane ;
 *  - **le premier plan** (`AppState`) : chaque passage, pendant la salle ;
 *  - **CallKeep qui ne démarre pas** : au natif, la salle ne se connecte qu'une fois `isCallKeepOn`
 *    (`awaitingCallKeep`). Passé `CALL_KEEP_GRACE_MS` à l'attendre, jeton là, une erreur
 *    `call_keep_not_started`, une fois par entrée : un tuteur bloqué sur « Connexion… » laisse une trace.
 *
 * La caméra et le micro demandés à l'entrée ont leurs fils d'Ariane dans `useTutorLive`. En développement,
 * rien ne part à Bugsnag (`bugsnagClient`) : la prochaine occurrence sur un émulateur se lit dans `adb logcat`.
 */
import { LogLevel, setLogExtension } from "livekit-client"
import { useEffect, useRef } from "react"
import { AppState } from "react-native"
import { useSelector } from "react-redux"

import { causeMessageOf } from "../../shared/nonError"
import { IS_WEB } from "../../shared/platform"
import { type EngineTelemetry, type TelemetryData } from "../../shared/telemetry"
import { awaitingCallKeep, CALL_KEEP_GRACE_MS } from "./tutorRoomRules"

/** Les lignes de LiveKit gardées par entrée dans la salle ; la suivante dit seulement que le plafond est atteint. */
export const LIVEKIT_LOG_LINES_MAX = 30
/** Les clés du contexte d'une ligne gardées, et la longueur de la ligne gardée comme événement. */
const LIVEKIT_CONTEXT_KEYS_MAX = 8
const LIVEKIT_EVENT_MAX = 120
/** Une clé qui porterait un nom (`participantName` compris : `cleanMetadata` ne lit que `_name`). */
const PERSONAL_KEY = /name|email|phone/i

type LiveKitLogSink = (level: LogLevel, message: unknown, context?: object) => void

/** La salle montée qui reçoit les lignes de LiveKit, s'il y en a une. */
let liveKitLogSink: LiveKitLogSink | null = null
let liveKitLogsInstalled = false

function installLiveKitLogs(): void {
    if (liveKitLogsInstalled) return
    liveKitLogsInstalled = true
    setLogExtension((level, message, context) => liveKitLogSink?.(level, message, context))
}

/**
 * Le contexte d'une ligne de LiveKit, en métadonnées : les valeurs simples et le message d'une erreur, sans
 * jeton (`causeMessageOf`) ni clé de nom, huit clés au plus. Les objets (une piste, un participant) sont laissés.
 */
export function liveKitLogData(context: object | undefined): TelemetryData {
    const data: TelemetryData = {}
    if (context == null) return data
    let kept = 0
    for (const [key, value] of Object.entries(context)) {
        if (kept >= LIVEKIT_CONTEXT_KEYS_MAX) break
        if (PERSONAL_KEY.test(key)) continue
        if (typeof value === "string") data[key] = causeMessageOf(value)
        else if (typeof value === "number" || typeof value === "boolean") data[key] = value
        else if (value instanceof Error) data[key] = causeMessageOf(`${value.name}: ${value.message}`)
        else continue
        kept += 1
    }
    return data
}

type RootState = {
    liveKitRoom?: { roomData?: { token?: string | null } | null }
    liveMode?: { isCallKeepOn?: boolean | null; hasPhonePermission?: boolean | null }
}

export function useTutorLiveTraces(telemetry: EngineTelemetry, liveSessionId: number | null): void {
    /* ── les journaux de LiveKit ── */
    useEffect(() => {
        installLiveKitLogs()
        let lines = 0
        const sink: LiveKitLogSink = (level, message, context) => {
            if (level < LogLevel.warn || level >= LogLevel.silent) return
            lines += 1
            if (lines > LIVEKIT_LOG_LINES_MAX) {
                if (lines === LIVEKIT_LOG_LINES_MAX + 1) {
                    telemetry.transition("livekit", "muted", "log_lines_max", { max: LIVEKIT_LOG_LINES_MAX })
                }
                return
            }
            const line = causeMessageOf(typeof message === "string" ? message : String(message))
            telemetry.transition(
                "livekit",
                level >= LogLevel.error ? "error" : "warn",
                line.slice(0, LIVEKIT_EVENT_MAX),
                liveKitLogData(context)
            )
        }
        liveKitLogSink = sink
        return () => {
            if (liveKitLogSink === sink) liveKitLogSink = null
        }
    }, [telemetry])

    /* ── le premier plan ── */
    useEffect(() => {
        let previous: string = AppState.currentState ?? "unknown"
        const subscription = AppState.addEventListener("change", (next) => {
            if (next === previous) return
            telemetry.transition(previous, next, "app_state")
            previous = next
        })
        return () => subscription.remove()
    }, [telemetry])

    /* ── CallKeep ── */
    const hasToken = useSelector((state: RootState) => !!state.liveKitRoom?.roomData?.token)
    const callKeepOn = useSelector((state: RootState) => state.liveMode?.isCallKeepOn ?? null)
    const hasPhonePermission = useSelector((state: RootState) => state.liveMode?.hasPhonePermission ?? null)
    const awaited = awaitingCallKeep({ isWeb: IS_WEB, hasToken, hasPhonePermission, callKeepOn })
    /** La séance dont l'attente a déjà été signalée : une erreur par entrée. */
    const reportedForRef = useRef<{ liveSessionId: number | null } | null>(null)
    useEffect(() => {
        if (!awaited) return undefined
        const startedAt = Date.now()
        telemetry.transition("call_keep_off", "awaiting_call_keep", "room_token")
        const timer = setTimeout(() => {
            if (reportedForRef.current?.liveSessionId === liveSessionId) return
            reportedForRef.current = { liveSessionId }
            telemetry.error("call_keep_not_started", null, {
                liveSessionId,
                waitedMs: Date.now() - startedAt,
                appState: AppState.currentState ?? null,
            })
        }, CALL_KEEP_GRACE_MS)
        return () => {
            clearTimeout(timer)
            telemetry.transition("awaiting_call_keep", "call_keep_settled", "call_keep", {
                waitedMs: Date.now() - startedAt,
            })
        }
    }, [awaited, liveSessionId, telemetry])
}
